import React from "react";
import { Shield, Lock, Eye, FileText } from "lucide-react";
import { Helmet } from "react-helmet-async";

export function PrivacyPolicy() {
  return (
    <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Privacy Policy – 247 DevTools Hub</title>
        <meta name="description" content="Privacy Policy for 247 DevTools Hub. Learn how we protect your developer data and ensure absolute confidentiality." />
      </Helmet>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-10 space-y-8">
        
        {/* Header */}
        <div className="text-center border-b border-slate-100 pb-8">
          <div className="inline-flex p-3 bg-blue-50 text-blue-600 rounded-full mb-4">
            <Shield className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Privacy Policy</h1>
          <p className="text-sm text-slate-500 mt-2">Last updated: July 16, 2026</p>
        </div>

        {/* Quick Summary Highlights */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex flex-col items-center text-center">
            <Lock className="w-6 h-6 text-blue-600 mb-2" />
            <h3 className="font-semibold text-slate-900 text-sm">100% Client-Side</h3>
            <p className="text-xs text-slate-500 mt-1">All data processing occurs entirely within your web browser.</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex flex-col items-center text-center">
            <Eye className="w-6 h-6 text-blue-600 mb-2" />
            <h3 className="font-semibold text-slate-900 text-sm">No Server Logs</h3>
            <p className="text-xs text-slate-500 mt-1">We do not store or transmit your pasted code, inputs, or CSVs.</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex flex-col items-center text-center">
            <FileText className="w-6 h-6 text-blue-600 mb-2" />
            <h3 className="font-semibold text-slate-900 text-sm">AdSense Compliant</h3>
            <p className="text-xs text-slate-500 mt-1">Transparency regarding cookie usage and partner ads.</p>
          </div>
        </div>

        {/* Detailed Sections */}
        <div className="prose prose-slate max-w-none text-slate-600 space-y-6 text-sm leading-relaxed">
          
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">1. Overview and Commitment</h2>
            <p>
              At <strong>247 DevTools Hub</strong>, we are committed to providing fast, reliable, browser-based utility tools without compromising your privacy. Because your security and trust are paramount, our architecture is designed as an <strong>offline-first, browser-only client application</strong>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">2. How Data is Processed (Zero Storage)</h2>
            <p>
              Unlike traditional SaaS platforms, <strong>we do NOT send your text, code, passwords, keys, CSVs, or Excel files to our servers</strong>. 
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>All cryptographic hashing, code formatting, search filtering, spreadsheet editing, and text conversion algorithms are executed locally inside your browser using client-side JavaScript.</li>
              <li>When you paste sensitive payloads (e.g. JSON blocks, raw strings, passwords to check), they remain strictly inside your device's memory (RAM).</li>
              <li>Once you refresh or close the tab, all active session data is completely purged.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">3. Google AdSense and DoubleClick Cookie</h2>
            <p>
              We run third-party advertisements via <strong>Google AdSense</strong> to support the free hosting and development of these utilities.
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-2">
              <li>Google, as a third-party vendor, uses cookies to serve ads on our site.</li>
              <li>Google's use of the advertising cookie enables it and its partners to serve ads to our users based on their visit to our site and other sites on the Internet.</li>
              <li>Users may opt-out of personalized advertising by visiting Google's <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Ads Settings</a> page. Alternatively, you can opt out of a third-party vendor's use of cookies for personalized advertising by visiting <a href="https://www.aboutads.info" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">aboutads.info</a>.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">4. Analytics and Cookies</h2>
            <p>
              To optimize tool layouts and understand general usage patterns, we may collect anonymous, aggregated telemetry (such as screen size, landing pages, and country region) using standard diagnostic utilities. <strong>We absolutely never link this diagnostic metadata with the payloads or code snippets you input.</strong>
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">5. External Links</h2>
            <p>
              Our website contains links to other developer tools and informational materials. Please be aware that we are not responsible for the privacy practices of external web assets. We encourage you to read their respective privacy guidelines.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-2">6. Contact Information</h2>
            <p>
              If you have any questions or security audits regarding our local-first implementation, please feel free to reach out to us via our designated <a href="/contact" className="text-blue-600 hover:underline">Contact Form</a>.
            </p>
          </section>

        </div>

      </div>
    </div>
  );
}
